import { useState, useEffect, useCallback, useRef } from 'react';
import axios from 'axios';
import { useLocation } from 'react-router-dom';
import { packageApiCall } from '../utils/renderFunctions';

export const useCustomerData = externalSetCustomerData => {
  const location = useLocation();
  const [packageName, setPackageName] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [customerData, setCustomerData] = useState({
    first_name: '',
    last_name: '',
    id: '',
    email: '',
    phone: '',
    height: '',
    weight: '',
    body_shape: '',
    preferred_style: '',
    chat_sessions_available: 0,
    video_sessions_available: 0,
    total_chat_sessions: 0,
    total_video_sessions: 0,
  });

  // Use external setter if provided, otherwise use internal
  const updateCustomerData = externalSetCustomerData || setCustomerData;

  // Create refs to avoid stale closures in useEffect
  const updateCustomerDataRef = useRef(updateCustomerData);
  updateCustomerDataRef.current = updateCustomerData;

  // Customer data fetching logic
  const fetchCustomerData = useCallback(
    async data => {
      if (!data?.id) return;

      setIsLoading(true);
      setError(null);

      try {
        const response = await axios.get(`${process.env.REACT_APP_BASE_URL}/customer/${data?.id}`);
        const updatedData = {
          first_name: data.first_name ?? response?.data?.customer?.first_name ?? '',
          last_name: data.last_name ?? response?.data?.customer?.last_name ?? '',
          id: response?.data?.customer?.id ?? data?.id,
          email: response?.data?.customer?.email ?? data?.email ?? '',
          phone: response?.data?.customer?.phone ?? data?.phone ?? '',
          height: response?.data?.customer?.height ?? '',
          weight: response?.data?.customer?.weight ?? '',
          body_shape: response?.data?.customer?.body_shape ?? '',
          preferred_style: response?.data?.customer?.preferred_style ?? '',
          chat_sessions_available: response?.data?.customer?.chat_sessions_available ?? 0,
          video_sessions_available: response?.data?.customer?.video_sessions_available ?? 0,
          total_chat_sessions: response?.data?.customer?.total_chat_sessions ?? 0,
          total_video_sessions: response?.data?.customer?.total_video_sessions ?? 0,
        };

        updateCustomerDataRef.current(updatedData);

        const packageResponse = await packageApiCall(response?.data?.customer?.id ?? data?.id);

        if (packageResponse?.data?.message === "You don't have plan") {
          setPackageName("You don't have plan");
        } else {
          setPackageName(packageResponse?.data?.package_name);
        }
      } catch (err) {
        setError('Failed to fetch customer data. Please try again.');
        // Handle error appropriately in production
      } finally {
        setIsLoading(false);
      }
    },
    [] // Remove dependency since we're using ref
  );

  const clearError = useCallback(() => {
    setError(null);
  }, []);

  // Create ref for fetchCustomerData to avoid stale closure
  const fetchCustomerDataRef = useRef(fetchCustomerData);
  fetchCustomerDataRef.current = fetchCustomerData;

  // Customer data fetching logic
  useEffect(() => {
    const handleCustomerData = async data => {
      await fetchCustomerDataRef.current(data);
    };

    const handleUserShopifyData = () => {
      const parameters = {};
      let hash;
      const q = location.search.split('?')[1];
      if (q !== undefined) {
        const qParams = q.split('&');
        for (let i = 0; i < qParams.length; i++) {
          hash = qParams[i].split('=');
          parameters[hash[0]] = hash[1];
        }
      }

      // Debug: logging URL parameters for development
      // eslint-disable-next-line no-console
      if (process.env.NODE_ENV === 'development') console.log('parameters=', parameters);

      const userValues = {
        id: parameters?.id,
        email: parameters?.email,
        phone: parameters?.phone,
        height: '',
        weight: '',
        first_name: parameters?.first_name,
        last_name: parameters?.last_name,
        preferred_style: '',
        body_shape: '',
        chat_sessions_available: 0,
        video_sessions_available: 0,
        total_chat_sessions: 0,
        total_video_sessions: 0,
      };

      updateCustomerDataRef.current(userValues);
      handleCustomerData(userValues);
    };

    handleUserShopifyData();
  }, [location.search]); // Remove updateCustomerData and fetchCustomerData from dependencies

  return {
    customerData,
    setCustomerData: updateCustomerData,
    packageName,
    setPackageName,
    isLoading,
    error,
    fetchCustomerData,
    clearError,
  };
};
